// Match action failure → i18n copy mapping.
//
// A rejected word-submit OR place-anchor POST returns a 409 STATE_CONFLICT
// whose body is `{ error: { details: { stage, reason } } }`. This module turns
// that machine reason into the most specific user-facing message id, falling
// back to a neutral generic key for unmapped reasons / non-409 failures. It is
// deliberately free of React/Pixi imports so it can be unit-tested cheaply
// (ARCA-599 lifted the word-submit helpers here out of MatchScreen so the
// place-anchor path could mirror them without dragging the whole component in).

import { ApiError } from '../../net/client'

/**
 * Pull the `details.reason` string out of a STATE_CONFLICT `ApiError` body, or
 * null when the error is not an ApiError / carries no reason. Defensive at
 * every hop because the body is typed `unknown`.
 */
export function stateConflictReason(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null
  const body = err.body
  if (typeof body !== 'object' || body === null) return null
  const error = (body as { error?: unknown }).error
  if (typeof error !== 'object' || error === null) return null
  const details = (error as { details?: unknown }).details
  if (typeof details !== 'object' || details === null) return null
  const reason = (details as { reason?: unknown }).reason
  return typeof reason === 'string' ? reason : null
}

// ARCA-145 (Bug D) — word-submit reasons we have dedicated copy for; every
// other reason (and any non-409 failure) falls back to the neutral generic key.
export const SUBMIT_REASON_MESSAGE_KEYS: Readonly<Record<string, string>> = {
  cell_frozen_by_tongue_tie: 'match.error.invalid_trace_frozen',
  // ARCA-630 — a word path that lists a Hollow cell 409s with
  // `path_through_hollow` (a diagonal squeezing PAST a Hollow corner is now
  // legal — ARCA-617 reversed). The trace-time guard (WordTraceLayer) normally
  // prevents tracing onto a Hollow, so this mapping is defense-in-depth (stale
  // board / race): surface the specific Hollow copy instead of the generic
  // "something went wrong" dead-end.
  path_through_hollow: 'match.error.invalid_trace_hollow',
}

/** Map a turn-step submit failure to the most specific i18n message id. */
export function submitErrorMessageId(err: unknown): string {
  const reason = stateConflictReason(err)
  // `Object.hasOwn` (not `in`) so a server-controlled reason can never match an
  // inherited Object.prototype key like "constructor".
  if (reason !== null && Object.hasOwn(SUBMIT_REASON_MESSAGE_KEYS, reason)) {
    return SUBMIT_REASON_MESSAGE_KEYS[reason] as string
  }
  return 'match.error.submit_failed'
}

// ARCA-599 (ARCA-590) — place-anchor reasons. `validateAnchorPlacement` only
// throws cell_off_field / cell_already_anchor / cell_already_hollow /
// cell_has_piece; of these only `cell_has_piece` has reachable dedicated copy
// (the UI now hides anchor/Hollow/off-field cells from the eligible set, so a
// well-behaved client never POSTs them). The rest fall back to the generic key.
export const ANCHOR_REASON_MESSAGE_KEYS: Readonly<Record<string, string>> = {
  cell_has_piece: 'match.error.anchor_cell_occupied',
}

/**
 * Banner + assertive-announce message ids for a place-anchor failure.
 *
 * A mapped reason (cell_has_piece) gets its dedicated copy on BOTH surfaces so
 * a screen-reader user hears the same specific guidance the sighted banner
 * shows. Every other reason keeps the prior behaviour: the generic banner key
 * plus the SR-dedicated announce key.
 */
export function anchorFailureMessageIds(err: unknown): {
  bannerId: string
  announceId: string
} {
  const reason = stateConflictReason(err)
  if (reason !== null && Object.hasOwn(ANCHOR_REASON_MESSAGE_KEYS, reason)) {
    const id = ANCHOR_REASON_MESSAGE_KEYS[reason] as string
    return { bannerId: id, announceId: id }
  }
  return {
    bannerId: 'match.error.submit_failed',
    announceId: 'a11y.announce.submit_failure',
  }
}
